import axios from 'axios';

/** Ordered time block within a day's timetable (maps to a single DB row). */
export interface TimetableBlock {
  type: 'period' | 'break';
  order: number;
  startTime: string;
  endTime: string;
  subjectId: string | number | null;
  teacherId: string | number | null;
}

export interface TimetableEntry {
  id?: number;
  schoolId?: number;
  classId: number;
  className?: string;
  section?: string;
  dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  periodNumber: number;
  timeSlot: string;
  subjectId?: number | null;
  subjectName?: string;
  subjectCode?: string;
  teacherId?: number | null;
  teacherName?: string;
  isBreak: boolean;
  breakName?: string;
}

/** Convert a persisted entry to the ordered block representation. */
export const entryToBlock = (entry: TimetableEntry, order: number): TimetableBlock => {
  const [startTime = '', endTime = ''] = entry.timeSlot.split('-').map((s) => s.trim());
  return {
    type: entry.isBreak ? 'break' : 'period',
    order,
    startTime,
    endTime,
    subjectId: entry.subjectId ?? null,
    teacherId: entry.teacherId ?? null,
  };
};

/** Convert a block back to the API payload shape. */
export const blockToEntry = (
  block: TimetableBlock,
  classId: number,
  dayOfWeek: TimetableEntry['dayOfWeek'],
  periodNumber: number
): TimetableEntry => ({
  classId,
  dayOfWeek,
  periodNumber,
  timeSlot: `${block.startTime} - ${block.endTime}`,
  subjectId: block.type === 'period' && block.subjectId ? Number(block.subjectId) : null,
  teacherId: block.type === 'period' && block.teacherId ? Number(block.teacherId) : null,
  isBreak: block.type === 'break',
  breakName: block.type === 'break' ? 'Break' : undefined,
});

const API_URL = `${process.env.REACT_APP_API_URL}/timetable`;

// Request interceptor to attach JWT token
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  };
};

export const saveTimetable = async (
  classId: number,
  day: string,
  entries: TimetableEntry[]
): Promise<TimetableEntry[]> => {
  try {
    const response = await axios.post(
      `${API_URL}/save?classId=${classId}&day=${day}`,
      entries,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTimetable = async (
  classId: number,
  day: string
): Promise<TimetableEntry[]> => {
  try {
    const response = await axios.get(
      `${API_URL}?classId=${classId}&day=${day}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTeacherSchedule = async (
  teacherId: number,
  day: string
): Promise<TimetableEntry[]> => {
  try {
    const response = await axios.get(
      `${API_URL}/teacher?teacherId=${teacherId}&day=${day}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTeacherWeekSchedule = async (
  teacherId: number
): Promise<TimetableEntry[]> => {
  try {
    const response = await axios.get(
      `${API_URL}/teacher/week?teacherId=${teacherId}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getClassWeekTimetable = async (
  classId: number
): Promise<TimetableEntry[]> => {
  try {
    const response = await axios.get(
      `${API_URL}/class/week?classId=${classId}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
